import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Lock, Sparkles, ArrowRight, RotateCcw, Compass, Brain, Heart, Briefcase, Star, Home, GraduationCap, Globe2, MapPin, FileText } from "lucide-react";
import { toast } from "sonner";
import { getUser, clearAuth, authFetch } from "@/lib/auth";
import AuthModal from "@/components/AuthModal";
import { downloadDiscoveryPdf } from "@/lib/downloads";
import { LangToggle, useLang } from "@/components/landing/i18n";

const slide = { initial: { opacity: 0, x: 36 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: -36 }, transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] } };

const MODULE_ICONS = { personality: Brain, interests: Compass, aptitude: Sparkles, workstyle: Briefcase, values: Heart };
const ACCENTS = ["#1F5BFF", "#17B6E0", "#7C3AED", "#E84393", "#F5A524", "#3AA64B"];

const Bar = ({ label, pct, color, right }) => (
  <div className="flex items-center gap-3">
    <span className="w-36 sm:w-44 text-xs font-semibold text-ink/65 text-right shrink-0 truncate">{label}</span>
    <div className="flex-1 h-2.5 rounded-full bg-ink/8 overflow-hidden">
      <motion.div className="h-full rounded-full" style={{ background: color }} initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }} />
    </div>
    <span className="w-14 text-xs font-bold shrink-0" style={{ color }}>{right ?? `${pct}%`}</span>
  </div>
);

const Section = ({ title, children, testid }) => (
  <div className="rounded-3xl bg-cream border border-ink/10 p-7" data-testid={testid}>
    <div className="font-display font-bold text-lg tracking-tight mb-5">{title}</div>
    {children}
  </div>
);
const DISCOVERY_UI = {
  en: {
    dashboard: "Dashboard", gateTitle: "The BrightIQ Discovery Assessment",
    gateCopy: "Five modules — personality, career interests, aptitude, work style and values — combining into your BrightIQ archetype and weighted career-fit map. Sign in to begin.",
    signin: "Sign in / Create free account", tag: "Career & Psychometric Discovery",
    introTitle: "Discover how you think, what motivates you, and which paths may fit you best",
    introCopy: "{total} questions across five modules, one at a time. About 20 minutes. You can leave and resume anytime — progress saves automatically.",
    begin: "Begin the assessment", chooseAnswer: "Choose the best answer", disclaimer: "A guidance and self-discovery tool — not a clinical diagnosis. Fit scores are exploratory, not guarantees.",
    notFound: "Discovery report not found", startFail: "Could not start", answerFail: "Something went wrong", network: "Network error",
    complete: "Assessment complete — your profile is ready!", downloaded: "Discovery PDF downloaded", pdfFail: "PDF download failed",
    archetype: "Your BrightIQ Archetype", alsoStrong: "Also strong", aptitudeOverall: "Aptitude overall", personality: "Personality spectrum", aptitude: "Aptitude profile",
    interests: "Career interests — 14 domains", clusters: "Top career clusters — weighted fit", fit: "fit", suggested: "Suggested courses", values: "Values & motivation",
    prefs: "Work & study preferences", strengths: "Key strengths", growth: "Development opportunities", environments: "Environments where you may thrive",
    next: "What to explore next", india: "Study / work in India", abroad: "Study / work abroad", dashboardBtn: "Dashboard", download: "Download PDF", retake: "Retake",
  },
  hi: {
    dashboard: "डैशबोर्ड", gateTitle: "BrightIQ खोज आकलन",
    gateCopy: "पाँच मॉड्यूल — व्यक्तित्व, करियर रुचियाँ, योग्यता, कार्य-शैली और मूल्य — मिलकर आपका BrightIQ आर्किटाइप और करियर-फिट नक्शा बनाते हैं। शुरू करने के लिए साइन इन करें।",
    signin: "साइन इन / मुफ़्त खाता बनाएँ", tag: "करियर और मनोवैज्ञानिक खोज",
    introTitle: "जानिए आप कैसे सोचते हैं, किससे प्रेरित होते हैं और कौन-से रास्ते आपको सबसे अच्छे सूट कर सकते हैं",
    introCopy: "पाँच मॉड्यूल में कुल {total} प्रश्न, एक-एक करके। लगभग 20 मिनट। आप कभी भी रुककर बाद में जारी कर सकते हैं — प्रगति अपने-आप सेव होती है।",
    begin: "आकलन शुरू करें", chooseAnswer: "सबसे सही उत्तर चुनें", disclaimer: "यह मार्गदर्शन और आत्म-खोज का साधन है — कोई मेडिकल निदान नहीं। फिट स्कोर खोजपूर्ण संकेत हैं, गारंटी नहीं।",
    notFound: "खोज रिपोर्ट नहीं मिली", startFail: "शुरू नहीं हो सका", answerFail: "कुछ गड़बड़ हुई", network: "नेटवर्क त्रुटि",
    complete: "आकलन पूरा हुआ — आपकी प्रोफ़ाइल तैयार है!", downloaded: "Discovery PDF डाउनलोड हुई", pdfFail: "PDF डाउनलोड नहीं हो सकी",
    archetype: "आपका BrightIQ आर्किटाइप", alsoStrong: "दूसरी प्रबल पहचान", aptitudeOverall: "कुल योग्यता", personality: "व्यक्तित्व स्पेक्ट्रम", aptitude: "योग्यता प्रोफ़ाइल",
    interests: "करियर रुचियाँ — 14 क्षेत्र", clusters: "शीर्ष करियर क्लस्टर — भारित फिट", fit: "फिट", suggested: "सुझाए गए कोर्स", values: "मूल्य और प्रेरणा",
    prefs: "कार्य और अध्ययन पसंद", strengths: "मुख्य ताकतें", growth: "विकास के क्षेत्र", environments: "ऐसे वातावरण जहाँ आप बेहतर कर सकते हैं",
    next: "आगे क्या जाँचें", india: "भारत में पढ़ाई / काम", abroad: "विदेश में पढ़ाई / काम", dashboardBtn: "डैशबोर्ड", download: "PDF डाउनलोड करें", retake: "फिर से करें",
  },
};


export default function DiscoveryPage() {
  const [user, setUser] = useState(getUser());
  const [authOpen, setAuthOpen] = useState(false);
  const [state, setState] = useState(null); // {item, idx, total...} | {done, reportId}
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [started, setStarted] = useState(false);
  const shownAt = useRef(Date.now());
const MODULE_LABELS_HI = { personality: "व्यक्तित्व प्रोफ़ाइल", interests: "करियर रुचियाँ", aptitude: "योग्यता", workstyle: "कार्य और अध्ययन शैली", values: "मूल्य और प्रेरणा" };
const MODULE_DESCRIPTIONS_HI = { personality: "ऊर्जा, सोच और फ़ैसले", interests: "14 करियर क्षेत्र", aptitude: "तर्क और समस्या-समाधान", workstyle: "आपके पसंदीदा वातावरण", values: "आपको चलाने वाली चीज़ें" };

  const navigate = useNavigate();
  const { reportId } = useParams();
  const { lang } = useLang();
  const ui = DISCOVERY_UI[lang];

  const load = useCallback(async () => {
    if (reportId) {
      const rr = await authFetch(`/api/discovery/report/${reportId}?lang=${lang}`);
      if (rr.status === 401) { clearAuth(); setUser(null); return; }
      if (!rr.ok) {
        toast.error(ui.notFound);
        navigate("/discovery", { replace: true });
        return;
      }
      setReport(await rr.json());
      setState({ done: true });
      return;
    }
    const res = await authFetch("/api/discovery/state");
    if (res.status === 401) { clearAuth(); setUser(null); return; }
    const d = await res.json();
    if (d.done) {
      const rr = await authFetch("/api/discovery/report");
      if (rr.ok) setReport(await rr.json());
      setState({ done: true });
    } else {
      setState(d);
      if (d.idx > 0) setStarted(true);
    }
  }, [navigate, reportId]);

  const currentItemId = state?.item?.id;
  useEffect(() => { if (user) load(); }, [user, load]);
  useEffect(() => { shownAt.current = Date.now(); }, [currentItemId]);

  const start = async () => {
    setBusy(true);
    const res = await authFetch(`/api/discovery/start?lang=${lang}`, { method: "POST" });
    const d = await res.json();
    if (!res.ok) toast.error(typeof d.detail === "string" ? d.detail : ui.startFail);
    else { setState(d); setStarted(true); }
    setBusy(false);
  };

  const answer = async (value) => {
    if (busy || !state?.item) return;
    setBusy(true);
    const ms = Date.now() - shownAt.current;
    try {
      const res = await authFetch("/api/discovery/answer", { method: "POST", body: JSON.stringify({ item_id: state.item.id, value, ms }) });
      const d = await res.json();
      if (!res.ok) toast.error(typeof d.detail === "string" ? d.detail : ui.answerFail);
      else if (d.done) {
        const rr = await authFetch("/api/discovery/report");
        if (rr.ok) setReport(await rr.json());
        setState({ done: true });
        toast.success(ui.complete);
        navigate("/discovery", { replace: true });
        window.scrollTo(0, 0);
      } else setState(d);
    } catch { toast.error(ui.network); }
    setBusy(false);
  };

  const retake = async () => {
    setBusy(true);
    const res = await authFetch(`/api/discovery/retake?lang=${lang}`, { method: "POST" });
    const d = await res.json();
    if (res.ok) { setReport(null); setState(d); setStarted(true); navigate("/discovery"); window.scrollTo(0, 0); }
    setBusy(false);
  };

  const downloadPdf = async () => {
    if (!report?.id) return;
    setBusy(true);
    try {
      await downloadDiscoveryPdf(report.id, `brightiq-discovery-${report.archetype.toLowerCase().replace(/\s+/g, "-")}.pdf`, lang);
      toast.success(ui.downloaded);
    } catch (err) {
      toast.error(err.message || ui.pdfFail);
    } finally {
      setBusy(false);
    }
  };


  const it = state?.item;
  const ModIcon = it ? MODULE_ICONS[it.module] : null;

  return (
    <div className="min-h-screen bg-paper text-ink font-body" data-testid="discovery-page">
      <header className="max-w-5xl mx-auto px-5 pt-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="discovery-home-logo">
          <img src="/brightiq-logo.png" alt="Bright IQ" className="h-10 w-10 rounded-lg object-contain" />
          <span className="font-display font-bold text-lg tracking-tight">Bright<span className="text-iqblue">IQ</span> <span className="text-ink/35 font-body text-[10px] font-semibold tracking-[0.2em] uppercase ml-1">Discovery</span></span>
        </Link>
        <div className="flex items-center gap-4">
          <LangToggle />
          {user && <Link to="/dashboard" className="text-sm font-semibold text-ink/60 hover:text-ink transition-colors" data-testid="discovery-dash-link">{ui.dashboard}</Link>}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-12">
        {!user && (
          <div className="rounded-[2rem] bg-cream border border-ink/10 p-10 text-center" data-testid="discovery-gate">
            <span className="inline-flex h-14 w-14 rounded-2xl bg-iqpurple/10 items-center justify-center"><Lock className="h-6 w-6 text-iqpurple" /></span>
            <h1 className="font-display font-bold text-3xl tracking-tight mt-6">{ui.gateTitle}</h1>
            <p className="mt-3 text-ink/60 text-sm sm:text-base max-w-md mx-auto leading-relaxed">{ui.gateCopy}</p>
            <button onClick={() => setAuthOpen(true)} data-testid="discovery-signin-button" className="mt-8 bg-ink text-paper font-semibold px-8 py-4 rounded-full hover:bg-iqblue transition-colors duration-300">
              {ui.signin}
            </button>
          </div>
        )}

        {user && !state && <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-iqblue" data-testid="discovery-loading" /></div>}

        {user && state && !state.done && !started && (
          <motion.div {...slide} className="rounded-[2rem] bg-cream border border-ink/10 p-8 sm:p-10" data-testid="discovery-intro">
            <div className="flex items-center gap-2 text-iqblue mb-4">
              <Sparkles className="h-4 w-4" />
              <span className="text-xs font-semibold tracking-[0.25em] uppercase">{ui.tag}</span>
            </div>
            <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">{ui.introTitle}</h1>
            <p className="mt-4 text-ink/60 text-sm sm:text-base leading-relaxed">{ui.introCopy.replace("{total}", state.total)}</p>
            <div className="mt-7 grid sm:grid-cols-2 gap-3">
              {state.modules.map((m, i2) => (
                <div key={m.key} className="flex items-start gap-3 rounded-2xl bg-white border border-ink/8 p-4" data-testid={`discovery-module-${m.key}`}>
                  <span className="font-display font-bold text-lg" style={{ color: ACCENTS[i2] }}>0{i2 + 1}</span>
                  <div><div className="font-semibold text-sm">{lang === "hi" ? MODULE_LABELS_HI[m.key] : m.label}</div><div className="text-xs text-ink/50 mt-0.5">{m.count} {lang === "hi" ? "प्रश्न" : "items"} · {lang === "hi" ? MODULE_DESCRIPTIONS_HI[m.key] : MODULE_DESCRIPTIONS[m.key]}</div></div>
                </div>
              ))}
            </div>
            <button onClick={start} disabled={busy} data-testid="discovery-start-button" className="group mt-8 flex items-center gap-2.5 bg-ink text-paper font-semibold px-8 py-4 rounded-full hover:bg-iqblue disabled:opacity-60 transition-colors duration-300">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{ui.begin} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></>}
            </button>
            <p className="mt-4 text-[11px] text-ink/40 leading-relaxed">{ui.disclaimer}</p>
          </motion.div>
        )}

        {user && state && !state.done && started && it && (
          <>
            <div className="flex items-center justify-between text-xs font-semibold tracking-[0.18em] uppercase text-ink/45 mb-3">
              <span className="flex items-center gap-2">{ModIcon && <ModIcon className="h-3.5 w-3.5 text-iqblue" />} {it.moduleLabel}</span>
              <span data-testid="discovery-progress-label">{state.idx + 1} / {state.total}</span>
            </div>
            <div className="h-1.5 rounded-full bg-ink/8 overflow-hidden mb-8">
              <motion.div data-testid="discovery-progress-bar" className="h-full bg-gradient-to-r from-iqblue via-iqpurple to-iqpink" animate={{ width: `${((state.idx + 1) / state.total) * 100}%` }} transition={{ duration: 0.4 }} />
            </div>
            <AnimatePresence mode="wait">
              <motion.div key={it.id} {...slide}>
                <h1 data-testid="discovery-question" className="font-display font-bold text-xl sm:text-2xl tracking-tight leading-snug">{it.text}</h1>
                {it.type === "likert" ? (
                  <div className="mt-8" data-testid="discovery-likert">
                    <div className="flex justify-between gap-1.5 sm:gap-2">
                      {[1, 2, 3, 4, 5, 6, 7].map((val) => (
                        <button
                          key={val}
                          data-testid={`discovery-likert-${val}`}
                          disabled={busy}
                          onClick={() => answer(val)}
                          title={it.labels[val - 1]}
                          className={`flex-1 h-11 sm:h-12 rounded-2xl border-2 text-sm font-bold transition-all duration-200 disabled:opacity-50 ${
                            val <= 2 ? "border-iqpink/30 text-iqpink hover:bg-iqpink hover:text-paper" :
                            val <= 4 ? "border-ink/15 text-ink/60 hover:bg-ink hover:text-paper" :
                            "border-iqgreen/30 text-iqgreen hover:bg-iqgreen hover:text-paper"
                          }`}
                        >
                          {val}
                        </button>
                      ))}
                    </div>
                    <div className="mt-2.5 flex justify-between text-[10px] font-semibold text-ink/40">
                      <span>{it.labels[0]}</span>
                      <span className="hidden sm:inline">{it.labels[3]}</span>
                      <span>{it.labels[6]}</span>
                    </div>
                  </div>
                ) : (
                  <div className="mt-8 space-y-2.5" data-testid="discovery-mcq">
                    {it.opts.map((opt, oi) => (
                      <button key={oi} data-testid={`discovery-option-${oi}`} disabled={busy} onClick={() => answer(oi)} className="w-full text-left rounded-2xl border border-ink/12 bg-cream px-5 py-4 text-sm font-medium hover:border-iqblue hover:bg-white hover:shadow-md active:scale-[0.99] disabled:opacity-50 transition-all duration-200">
                        {opt}
                      </button>
                    ))}
                  </div>
                )}
                {busy && <div className="mt-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-iqblue" /></div>}
              </motion.div>
            </AnimatePresence>
          </>
        )}

        {user && state?.done && report && (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="space-y-5 max-w-none" data-testid="discovery-results">
            <div className="rounded-[2rem] bg-ink text-paper p-8 sm:p-10 relative overflow-hidden">
              <div className="absolute inset-0 spotlight-glow pointer-events-none" aria-hidden />
              <div className="relative">
                <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-iqcyan">{ui.archetype}</div>
                <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight mt-3" data-testid="disc-archetype">{report.archetype}</h1>
                <p className="mt-2 font-serifa italic text-lg text-paper/70">{report.archetype_summary}</p>
                <p className="mt-3 text-xs text-paper/50">{ui.alsoStrong}: <b className="text-paper/75">{report.secondary}</b> — {report.secondary_summary}</p>
                <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-4 py-2 text-xs font-semibold">{ui.aptitudeOverall}: {report.aptitude_overall}%</div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <Section title={ui.personality} testid="disc-personality">
                <div className="space-y-3">
                  {Object.entries(report.personality).map(([k, val], i2) => (
                    <Bar key={k} label={(lang === "hi" ? CONSTRUCT_HI[k] : CONSTRUCT[k]) || k} pct={val} color={ACCENTS[i2 % 6]} />
                  ))}
                </div>
              </Section>
              <Section title={ui.aptitude} testid="disc-aptitude">
                <div className="space-y-3">
                  {Object.entries(report.aptitude).map(([k, val], i2) => (
                    <Bar key={k} label={(lang === "hi" ? AREAS_HI[k] : AREAS[k]) || k} pct={val} color={ACCENTS[(i2 + 2) % 6]} />
                  ))}
                </div>
              </Section>
            </div>

            <Section title={ui.interests} testid="disc-interests">
              <div className="space-y-2.5">
                {Object.entries(report.interests).sort((x, y) => y[1] - x[1]).map(([k, val], i2) => (
                  <Bar key={k} label={(lang === "hi" ? DOMAINS_HI[k] : DOMAINS[k]) || k} pct={val} color={ACCENTS[i2 % 6]} />
                ))}
              </div>
            </Section>

            <Section title={ui.clusters} testid="disc-clusters">
              <div className="space-y-4">
                {report.clusters.map((c, i2) => (
                  <div key={c.name} className="rounded-2xl bg-white border border-ink/8 p-5" data-testid={`disc-cluster-${i2}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <span className="font-display font-bold text-2xl" style={{ color: ACCENTS[i2 % 6] }}>{i2 + 1}</span>
                        <span className="font-display font-bold text-base tracking-tight">{c.name}</span>
                      </div>
                      <span className="text-xs font-bold rounded-full px-3 py-1.5 text-paper" style={{ background: ACCENTS[i2 % 6] }}>{c.fit}% {ui.fit}</span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {c.courses.map((course) => (
                        <span key={course} className="text-[11px] font-semibold bg-cream border border-ink/10 rounded-full px-3 py-1.5">{course}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-[11px] text-ink/40">{lang === "hi" ? "फिट = 40% रुचि · 25% व्यक्तित्व और कार्य-शैली · 15% सीखने की पसंद · 10% मूल्य · 10% वातावरण।" : "Fit = 40% interest · 25% personality & work style · 15% learning preferences · 10% values · 10% environment."}</p>
            </Section>

            <div className="grid sm:grid-cols-2 gap-5">
              <Section title={ui.prefs} testid="disc-prefs">
                <div className="space-y-3">
                  {Object.entries(report.prefs).map(([k, val]) => (
                    <Bar key={k} label={AXES_META[k]?.[0] || k} pct={val} color="#17B6E0" right={AXES_META[k] ? (val >= 50 ? AXES_META[k][0] : AXES_META[k][1]) : `${val}%`} />
                  ))}
                </div>
              </Section>
              <Section title="Values & motivation" testid="disc-values">
                <div className="space-y-3">
                  {Object.entries(report.values).sort((x, y) => y[1] - x[1]).map(([k, val], i2) => (
                    <Bar key={k} label={VALUES_META[k] || k} pct={val} color={ACCENTS[(i2 + 4) % 6]} />
                  ))}
                </div>
              </Section>
            </div>

            <div className="grid sm:grid-cols-3 gap-5">
              <Section title="Key strengths" testid="disc-strengths">
                <ul className="space-y-2.5">{report.strengths.map((s) => <li key={s} className="flex gap-2 text-sm text-ink/70"><Star className="h-4 w-4 text-iqamber shrink-0 mt-0.5" />{s}</li>)}</ul>
              </Section>
              <Section title="Development opportunities" testid="disc-growth">
                <ul className="space-y-2.5">{report.growth.map((s) => <li key={s} className="flex gap-2 text-sm text-ink/70"><Compass className="h-4 w-4 text-iqpink shrink-0 mt-0.5" />{s}</li>)}</ul>
              </Section>
              <Section title="Preferred environments" testid="disc-env">
                <ul className="space-y-2.5">{report.environments.map((s) => <li key={s} className="flex gap-2 text-sm text-ink/70"><Briefcase className="h-4 w-4 text-iqgreen shrink-0 mt-0.5" />{s}</li>)}</ul>
              </Section>
            </div>

            <Section title="Pathways — India & abroad" testid="disc-pathways">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className={`rounded-2xl border-2 p-5 ${report.study_location === "Domestic" ? "border-iqblue bg-iqblue/5" : "border-ink/10 bg-white"}`}>
                  <div className="flex items-center gap-2 font-display font-bold text-sm"><MapPin className="h-4 w-4 text-iqblue" /> Study in India {report.study_location === "Domestic" && <span className="text-[9px] font-bold uppercase tracking-wider bg-iqblue text-paper px-2 py-0.5 rounded-full">your plan</span>}</div>
                  <p className="mt-2.5 text-xs text-ink/60 leading-relaxed">Strong {report.clusters[0].name.toLowerCase()} programs at national institutes and universities; entrance routes and scholarships align with your {report.archetype} profile.</p>
                </div>
                <div className={`rounded-2xl border-2 p-5 ${report.study_location === "International" ? "border-iqpurple bg-iqpurple/5" : "border-ink/10 bg-white"}`}>
                  <div className="flex items-center gap-2 font-display font-bold text-sm"><Globe2 className="h-4 w-4 text-iqpurple" /> Study abroad {report.study_location === "International" && <span className="text-[9px] font-bold uppercase tracking-wider bg-iqpurple text-paper px-2 py-0.5 rounded-full">your plan</span>}</div>
                  <p className="mt-2.5 text-xs text-ink/60 leading-relaxed">Your profile suits programs with project-based learning; shortlist by {report.clusters[0].name.toLowerCase()} strength, scholarships and post-study pathways.</p>
                </div>
              </div>
            </Section>

            <Section title="What to explore next" testid="disc-next">
              <ul className="space-y-2.5">{report.explore_next.map((s, i2) => <li key={s} className="flex gap-3 text-sm text-ink/70"><span className="font-display font-bold" style={{ color: ACCENTS[i2 % 6] }}>{i2 + 1}.</span>{s}</li>)}</ul>
              <div className="mt-6 rounded-2xl bg-ink text-paper p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <p className="text-sm text-paper/80">Walk this report through with a BrightIQ counsellor — bring your top clusters and questions.</p>
                <Link to="/" className="shrink-0 bg-paper text-ink text-xs font-bold px-5 py-3 rounded-full hover:bg-iqcyan transition-colors" data-testid="disc-counselor-cta">Book a counsellor session</Link>
              </div>
            </Section>

            <div className="flex flex-wrap justify-center gap-3 pt-2">
              <Link to="/dashboard" data-testid="disc-dashboard-link" className="flex items-center gap-2 bg-ink text-paper font-semibold px-7 py-3.5 rounded-full hover:bg-iqblue transition-colors"><Home className="h-4 w-4" /> Dashboard</Link>
              <button onClick={downloadPdf} disabled={busy} data-testid="disc-download-pdf" className="flex items-center gap-2 bg-iqamber text-ink font-semibold px-7 py-3.5 rounded-full hover:brightness-110 disabled:opacity-60 transition-all">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Download PDF
              </button>
              <button onClick={retake} disabled={busy} data-testid="disc-retake-button" className="flex items-center gap-2 border border-ink/20 font-semibold px-7 py-3.5 rounded-full hover:bg-ink hover:text-paper disabled:opacity-60 transition-colors">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Retake
              </button>
            </div>

          </motion.div>
        )}
      </main>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} onSuccess={(u) => setUser(u)} />
    </div>
  );
}

const CONSTRUCT = { social_energy: "Social Energy", thinking: "Thinking Style", decision: "Decision-Making", planning: "Planning & Adaptability", resilience: "Resilience & Confidence" };
const DOMAINS = { tech: "Technology", engineering: "Engineering", science: "Science", business: "Business", finance: "Finance", entrepreneurship: "Entrepreneurship", law: "Law", psych: "Psychology & Human Services", education: "Education", healthcare: "Healthcare", media: "Media & Communication", design: "Design", public_affairs: "Public Affairs", social_impact: "Social Impact" };
const AREAS = { numerical: "Numerical", verbal: "Verbal", logical: "Logical", abstract: "Abstract", spatial: "Spatial", problem_solving: "Problem Solving" };
const AXES_META = { independent: ["Independent", "Collaborative"], structured: ["Structured", "Flexible"], competitive: ["Competitive", "Cooperative"], practical: ["Practical", "Theoretical"], stable: ["Stable", "Dynamic"], international: ["International", "Local"], autonomy: ["Autonomous", "Guided"], innovation: ["Innovation-driven", "Proven methods"] };
const VALUES_META = { achievement: "Achievement", financial: "Financial Success", security: "Security", autonomy_v: "Autonomy", purpose: "Purpose", recognition: "Recognition", learning: "Learning & Growth", creativity: "Creativity", contribution: "Social Contribution" };
const CONSTRUCT_HI = { social_energy: "सामाजिक ऊर्जा", thinking: "सोचने की शैली", decision: "निर्णय-क्षमता", planning: "योजना और अनुकूलन", resilience: "लचीलापन और आत्मविश्वास" };
const DOMAINS_HI = { tech: "प्रौद्योगिकी", engineering: "इंजीनियरिंग", science: "विज्ञान", business: "बिज़नेस", finance: "वित्त", entrepreneurship: "उद्यमिता", law: "कानून", psych: "मनोविज्ञान और मानव सेवाएँ", education: "शिक्षा", healthcare: "स्वास्थ्य सेवा", media: "मीडिया और संचार", design: "डिज़ाइन", public_affairs: "लोक प्रशासन", social_impact: "सामाजिक प्रभाव" };
const AREAS_HI = { numerical: "संख्यात्मक", verbal: "भाषाई", logical: "तार्किक", abstract: "अमूर्त", spatial: "स्थानिक", problem_solving: "समस्या-समाधान" };
const AXES_META_HI = { independent: ["स्वतंत्र", "सहयोगी"], structured: ["संरचित", "लचीला"], competitive: ["प्रतिस्पर्धी", "सहयोगी"], practical: ["व्यावहारिक", "सैद्धांतिक"], stable: ["स्थिर", "गतिशील"], international: ["अंतरराष्ट्रीय", "स्थानीय"], autonomy: ["स्वायत्त", "निर्देशित"], innovation: ["नवाचार-प्रेरित", "प्रमाणित तरीके"] };
const VALUES_META_HI = { achievement: "उपलब्धि", financial: "आर्थिक सफलता", security: "सुरक्षा", autonomy_v: "स्वायत्तता", purpose: "उद्देश्य", recognition: "मान्यता", learning: "सीखना और विकास", creativity: "रचनात्मकता", contribution: "सामाजिक योगदान" };


const MODULE_DESCRIPTIONS = { personality: "how you energize, think and decide", interests: "14 career domains", aptitude: "reasoning and problem solving", workstyle: "your preferred environments", values: "what drives you" };
